import React, { useState, useEffect, useRef } from 'react'
import styles from './JobPostFourthPageView.module.scss'
import PreviewModal from './PreviewModal'
import { useNavigate } from 'react-router-dom'
import { useAuthStatus } from '../../utils'
import axios from 'axios'
import { clearJobPostSessionStorage } from '../../utils/jobPostSessionStorage'

const TITLE_MAX_LEN = 40
const TITLE_SUFFIX = ' 캐디 모집'

/** ThirdPageData.selectedToursData 기준 기본 공고 제목 (40자 이내) */
function buildDefaultJobPostTitle(selectedToursData) {
	if (!Array.isArray(selectedToursData) || selectedToursData.length === 0) return ''
	const names = selectedToursData.map((t) => t?.name).filter(Boolean)
	if (names.length === 0) return ''

	if (names.length === 1) {
		let out = `${names[0]}${TITLE_SUFFIX}`
		if (out.length <= TITLE_MAX_LEN) return out
		const nameBudget = TITLE_MAX_LEN - TITLE_SUFFIX.length
		return `${names[0].slice(0, Math.max(0, nameBudget))}${TITLE_SUFFIX}`.slice(
			0,
			TITLE_MAX_LEN,
		)
	}

	const multiSuffix = ` 외 ${names.length - 1}경기${TITLE_SUFFIX}`
	let out = `${names[0]}${multiSuffix}`
	if (out.length <= TITLE_MAX_LEN) return out
	const nameBudget = TITLE_MAX_LEN - multiSuffix.length
	if (nameBudget < 1) return out.slice(0, TITLE_MAX_LEN)
	return `${names[0].slice(0, nameBudget)}${multiSuffix}`.slice(0, TITLE_MAX_LEN)
}

const JobPostFourthPageView = () => {
	const navigate = useNavigate()
	const [authStatus] = useAuthStatus()
	const [jobPostBriefLength, setJobPostBriefLength] = useState(0)
	const [jobPostTitleLength, setJobPostTitleLength] = useState(0)
	const [jobPostTitle, setJobPostTitle] = useState('')
	const [jobPostBrief, setJobPostBrief] = useState('')
	const [isNextButtonAvailable, setIsNextButtonAvailable] = useState(false)
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
	const [isSuccessMessageOpen, setIsSuccessMessageOpen] = useState(false)

	const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

	useEffect(() => {
		const raw = sessionStorage.getItem('ThirdPageData')
		if (!raw) return
		let data
		try {
			data = JSON.parse(raw)
		} catch {
			return
		}
		const suggested = buildDefaultJobPostTitle(data?.selectedToursData)
		if (!suggested) return
		setJobPostTitle((prev) => (prev.trim() === '' ? suggested : prev))
	}, [])

	useEffect(() => {
		setJobPostTitleLength(jobPostTitle.length)
	}, [jobPostTitle])

	useEffect(() => {
		if (jobPostTitleLength > 0 && jobPostBriefLength > 0) {
			setIsNextButtonAvailable(true)
		} else {
			setIsNextButtonAvailable(false)
		}
	}, [jobPostTitleLength, jobPostBriefLength])

	const modalRef = useRef(null)
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
	const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })
	const [hasBeenDragged, setHasBeenDragged] = useState(false)

	const handleMouseDown = (e) => {
		if (modalRef.current) {
			const rect = modalRef.current.getBoundingClientRect()
			setDragOffset({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			})
			setIsDragging(true)
		}
	}

	const handleMouseMove = (e) => {
		if (isDragging && modalRef.current) {
			const newX = e.clientX - dragOffset.x
			const newY = e.clientY - dragOffset.y

			// 화면 경계 체크
			const maxX = window.innerWidth - modalRef.current.offsetWidth
			const maxY = window.innerHeight - modalRef.current.offsetHeight

			const clampedX = Math.max(0, Math.min(newX, maxX))
			const clampedY = Math.max(0, Math.min(newY, maxY))

			modalRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`

			// 위치 업데이트
			setModalPosition({ x: clampedX, y: clampedY })
			setHasBeenDragged(true)
		}
	}

	const handleMouseUp = () => {
		setIsDragging(false)
	}

	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
			return () => {
				document.removeEventListener('mousemove', handleMouseMove)
				document.removeEventListener('mouseup', handleMouseUp)
			}
		}
	}, [isDragging, dragOffset])

	// 모달이 열릴 때 위치 설정
	useEffect(() => {
		if (isPreviewModalOpen && modalRef.current) {
			let targetX, targetY

			if (hasBeenDragged) {
				// 이전에 드래그한 위치가 있으면 그 위치 사용
				targetX = modalPosition.x
				targetY = modalPosition.y
			} else {
				// 처음 열리는 경우 화면 중앙에 위치
				const modalWidth = 360
				const modalHeight = 640
				targetX = (window.innerWidth - modalWidth) / 2
				targetY = (window.innerHeight - modalHeight) / 2
			}

			modalRef.current.style.transform = `translate(${targetX}px, ${targetY}px)`
		}
	}, [isPreviewModalOpen, hasBeenDragged, modalPosition])

	// 페이지를 벗어날 때 모달 위치 초기화
	useEffect(() => {
		return () => {
			setHasBeenDragged(false)
			setModalPosition({ x: 0, y: 0 })
		}
	}, [])

	const onSubmit = async () => {
		const dataFromThirdPageString = sessionStorage.getItem('ThirdPageData')
		const dataFromThirdPage = dataFromThirdPageString ? JSON.parse(dataFromThirdPageString) : {}
		console.log('dataFromThirdPage:', dataFromThirdPage)
		console.log('dataFromThirdPage.contractType:', dataFromThirdPage.contractType)
		// 데이터 처리 로직
		console.log('제출된 데이터:', { jobPostTitle, jobPostBrief })
		// 다음 페이지로 이동
		console.log('4차 authStatus:', authStatus)

		const userId = authStatus.user.id

		const contractType =
			dataFromThirdPage.contractType === 'short-contract'
				? 'single'
				: dataFromThirdPage.contractType === 'long-contract'
					? 'long_term'
					: dataFromThirdPage.periodType &&
						  dataFromThirdPage.periodType.includes('last') &&
						  dataFromThirdPage.periodType.includes('first')
						? 'both'
						: dataFromThirdPage.periodType &&
							  dataFromThirdPage.periodType.includes('last')
							? 'year_second_half'
							: dataFromThirdPage.periodType &&
								  dataFromThirdPage.periodType.includes('first')
								? 'year_first_half'
								: ''

		let contractStartDate = null
		let contractEndDate = null

		if (dataFromThirdPage.contractType === 'period-no-tour-first-half-last-half') {
			contractStartDate = new Date()
			contractEndDate = new Date(dataFromThirdPage.selectedYear + '-12-31')
		} else if (dataFromThirdPage.contractType === 'period-no-tour-first-half') {
			contractStartDate = new Date()
			contractEndDate = new Date(dataFromThirdPage.selectedYear + '-06-30')
		} else if (dataFromThirdPage.contractType === 'period-no-tour-last-half') {
			contractStartDate = new Date()
			contractEndDate = new Date(dataFromThirdPage.selectedYear + '-06-30')
		} else if (
			dataFromThirdPage.selectedToursData &&
			dataFromThirdPage.selectedToursData.length > 0
		) {
			console.log('대회선택됨')
			contractStartDate = new Date(dataFromThirdPage.selectedToursData[0].startDate)
			contractEndDate = new Date(
				dataFromThirdPage.selectedToursData[
					dataFromThirdPage.selectedToursData.length - 1
				].endDate,
			)
		}

		const dataForSendingServer = {
			userId: userId,
			postSubject: jobPostTitle,
			postContent: jobPostBrief,
			contractType: contractType,
			contractStartedAt: contractStartDate,
			contractEndedAt: contractEndDate,
			tourIds: dataFromThirdPage?.selectedToursData?.map((tour) => tour.id),
			contract: {},
		}
		if (
			dataForSendingServer.contractType === 'single' ||
			dataForSendingServer.contractType === 'long_term'
		) {
			if (dataFromThirdPage.isDomesticContract) {
				const dataFromThirdPageDomesticContract = dataFromThirdPage.domesticFormData
				const dataForSendingServerDomesticContract = {
					isSalaryTaxIncluded:
						dataFromThirdPageDomesticContract.applyTax === 'exclude' ? false : true,
					payrollCycles:
						dataFromThirdPageDomesticContract.payPeriod === 'weekly'
							? 'weekly'
							: 'perGame',
					weeklySalaryCutThrough: Number(dataFromThirdPageDomesticContract.cutPass),
					weeklySalaryCutFail: Number(dataFromThirdPageDomesticContract.cutOut),
					jejuTournamentParticipation: dataFromThirdPageDomesticContract.isTourJejudo,
					expenseReimbursementJeju: dataFromThirdPageDomesticContract.isTourJejudo
						? Number(dataFromThirdPageDomesticContract.jejudoSupport)
						: 0,
					wearSponsoredHat: dataFromThirdPageDomesticContract.sponsorCap,
					incentives: dataFromThirdPageDomesticContract.incentiveSettings.map(
						(incentive) => ({
							startRank: incentive.start,
							endRank: incentive.end === 'CUT' ? 0 : incentive.end,
							percentage: incentive.percentage,
						}),
					),
					// 여기 인센티브 세금 옵션 필요
				}
				dataForSendingServer.contract.domesticContract =
					dataForSendingServerDomesticContract
			}
			if (dataFromThirdPage.isOverseasContract) {
				const dataFromThirdPageOverseasContract = dataFromThirdPage.overseasFormData
				const dataForSendingServerOverseasContract = {
					isSalaryTaxIncluded:
						dataFromThirdPageOverseasContract.applyTax === 'exclude' ? false : true,
					payrollCycles:
						dataFromThirdPageOverseasContract.payPeriod === 'weekly'
							? 'weekly'
							: 'perGame',
					weeklySalaryCutThrough: Number(dataFromThirdPageOverseasContract.cutPass),
					weeklySalaryCutFail: Number(dataFromThirdPageOverseasContract.cutOut),
					wearSponsoredHat: dataFromThirdPageOverseasContract.sponsorCap,
					expenseReimbursement: dataFromThirdPageOverseasContract.overseasSupport,
					incentives: dataFromThirdPageOverseasContract.incentiveSettings.map(
						(incentive) => ({
							startRank: incentive.start,
							endRank: incentive.end === 'CUT' ? 0 : incentive.end,
							percentage: incentive.percentage,
						}),
					),
					// 여기 인센티브 세금 옵션 필요
				}
				dataForSendingServer.contract.overseasContract =
					dataForSendingServerOverseasContract
			}
		} else {
			const dataFromThirdPagePeriodContract = dataFromThirdPage.generalFormData
			const dataForSendingServerPeriodContract = {
				isSalaryTaxIncluded:
					dataFromThirdPagePeriodContract.applyTax === 'exclude' ? false : true,
				payrollCycles:
					dataFromThirdPagePeriodContract.payPeriod === 'weekly' ? 'weekly' : 'perGame',
				salaryAmountForPeriod: Number(dataFromThirdPagePeriodContract.periodSalary),
				isOverseasIncluded: dataFromThirdPagePeriodContract.overseasTour,
				expenseReimbursement: dataFromThirdPagePeriodContract.overseasSupport,
				incentives: dataFromThirdPagePeriodContract.incentiveSettings.map((incentive) => ({
					startRank: incentive.start,
					endRank: incentive.end,
					percentage: incentive.percentage,
				})),
				// 여기 인센티브 세금 옵션 필요
				wearSponsoredHat: dataFromThirdPagePeriodContract.sponsorCap,
			}
			dataForSendingServer.contract.termContract = dataForSendingServerPeriodContract
		}

		if (
			dataFromThirdPage.contractType === 'short-contract' ||
			dataFromThirdPage.contractType === 'long-contract'
		) {
			if (dataFromThirdPage.isDomesticContract && dataFromThirdPage.isOverseasContract) {
				dataForSendingServer.region = 'BOTH'
			} else if (dataFromThirdPage.isDomesticContract) {
				dataForSendingServer.region = 'DOMESTIC'
			} else if (dataFromThirdPage.isOverseasContract) {
				dataForSendingServer.region = 'INTERNATIONAL'
			}
		}

		console.log('dataForSendingServer:', dataForSendingServer)

		try {
			const response = await axios.post(`${API_ENDPOINT}/job-post`, dataForSendingServer)
			console.log('response:', response)
			if (response.status >= 200 && response.status < 300) {
				clearJobPostSessionStorage()
				setIsSuccessMessageOpen(true)
				setTimeout(() => {
					setIsSuccessMessageOpen(false)
					navigate('/posts/manage')
				}, 1500)
			} else {
				alert('공고 작성에 실패했습니다.')
			}
		} catch (error) {
			console.error('Error:', error)
			alert('공고 작성에 실패했습니다.')
		}
	}
	return (
		<div className={styles.jobPostFourthPageContainer}>
			<div className={styles.jobPostFourthPageHeader}>
				<div className={styles.title}>공고 작성</div>
				<button
					className={styles.preview}
					onClick={(e) => {
						e.stopPropagation()
						if (isPreviewModalOpen) {
							setIsPreviewModalOpen(false)
						} else {
							setIsPreviewModalOpen(true)
						}
					}}>
					미리보기
				</button>
			</div>
			<div className={styles.progressBar}></div>
			<form className={styles.writeJobPostForm}>
				<div className={styles.writeJobPostFormTitle}>공고제목 및 소개를 입력해주세요</div>
				<div className={styles.writeJobPostFormContainer}>
					<div className={styles.writeJobTitle}>공고제목</div>
					<input
						type="text"
						name="jobPostTitle"
						className={styles.writeJobTitleInput}
						placeholder="예시 : KPGA 춘천오픈 5월 단기 캐디 모집"
						maxLength={40}
						autoComplete="off"
						value={jobPostTitle}
						onChange={(e) => {
							setJobPostTitle(e.target.value)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault()
							}
						}}
					/>
					<div className={styles.writeJobLabel}>간략소개</div>
					{isSuccessMessageOpen && (
						<div className={styles.successMessage}>
							<span>공고가 등록이 완료되었어요!</span>
						</div>
					)}
					<textarea
						name="jobPostBrief"
						className={styles.writeJobBriefInput}
						placeholder="예 : 책임감 있고 프로 정신 투철한 캐디 구합니다. 거리 측정 능숙자 우대. 경력자 환영 (500자 이내)"
						maxLength={500}
						autoComplete="off"
						value={jobPostBrief}
						onChange={(e) => {
							setJobPostBrief(e.target.value)
							setJobPostBriefLength(e.target.value.length)
						}}
					/>
					<div className={styles.jobPostBriefLength}>{jobPostBriefLength} / 500</div>
				</div>
			</form>
			<div className={styles.whatsNext}>
				<div className={styles.buttons}>
					<button className={styles.prevButton} onClick={() => navigate(-1)}>
						이전
					</button>
					<button
						className={`${styles.nextButton} ${
							isNextButtonAvailable ? '' : styles.nextButtonDisabled
						}`}
						onClick={() => {
							onSubmit()
						}}>
						다음
					</button>
				</div>
			</div>
			{isPreviewModalOpen && (
				<div
					ref={modalRef}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						zIndex: 1000,
						cursor: isDragging ? 'grabbing' : 'grab',
					}}
					onMouseDown={handleMouseDown}
					onClick={(e) => e.stopPropagation()}>
					<PreviewModal
						isOpen={isPreviewModalOpen}
						onClose={() => setIsPreviewModalOpen(false)}
						writtenData={{
							jobPostTitle,
							jobPostBrief,
						}}
					/>
				</div>
			)}
		</div>
	)
}

export default JobPostFourthPageView
